import { ChevronRightIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import {
  type PartyEmailDefaultEntry,
  compactPartyDefault,
  normalizePartyDefault,
} from 'components/EmailTo/applyEmailModalDefaults'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { type EmailModalId, EMAIL_MODAL_LABELS } from 'config/emailModalDefaults.constants'
import { getPartyLabelsForEmailModal } from 'config/emailModalPartyScope'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Checkbox } from 'stories/components'

export const EmailModalDefaultsConfig = () => {
  const [isLoading, setLoading] = useState(true)
  const [isChanged, setChanged] = useState(false)
  const [fullConfig, setFullConfig] = useState<Record<string, Record<string, PartyEmailDefaultEntry>>>({})
  const [selectedModal, setSelectedModal] = useState<string>('')

  useEffect(() => {
    setLoading(true)
    getAdminConfig('emailModalDefaults')
      .then((res) => setFullConfig(res && typeof res === 'object' ? res : {}))
      .finally(() => setLoading(false))
  }, [])

  const partyLabels = useMemo(() => {
    if (!selectedModal) return []
    return getPartyLabelsForEmailModal(selectedModal as EmailModalId)
  }, [selectedModal])

  const modalPartyMap = useMemo(() => {
    if (!selectedModal) return {}
    return fullConfig[selectedModal] || {}
  }, [fullConfig, selectedModal])

  const setPartyDir = (party: string, key: 'to' | 'cc' | 'bcc', checked: boolean) => {
    const next = cloneDeep(fullConfig)
    if (!next[selectedModal]) next[selectedModal] = {}
    const current = normalizePartyDefault(next[selectedModal][party])
    const dir = checked ? key : current.dir === key ? '' : current.dir
    next[selectedModal][party] = compactPartyDefault({ dir, hide: current.hide })
    setFullConfig(next)
    setChanged(true)
  }

  const setPartyHide = (party: string, checked: boolean) => {
    const next = cloneDeep(fullConfig)
    if (!next[selectedModal]) next[selectedModal] = {}
    const current = normalizePartyDefault(next[selectedModal][party])
    next[selectedModal][party] = compactPartyDefault({ dir: current.dir, hide: checked })
    setFullConfig(next)
    setChanged(true)
  }

  const onSave = () => {
    setLoading(true)
    setAdminConfig('emailModalDefaults', fullConfig)
      .then(() => toast('Default email recipients saved.', { type: 'success' }))
      .finally(() => {
        setLoading(false)
        setChanged(false)
      })
  }

  const modalKeys = Object.keys(EMAIL_MODAL_LABELS) as (keyof typeof EMAIL_MODAL_LABELS)[]

  return (
    <div>
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} onSave={onSave} />

      {!selectedModal ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {modalKeys.map((key, index) => (
            <div key={key} className="mb-2 flex items-center">
              <button
                type="button"
                className="w-full flex items-center justify-between gap-2 text-left rounded px-2 py-1.5 border border-transparent hover:border-gray-200 hover:bg-gray-50/80 transition-colors"
                onClick={() => setSelectedModal(key)}
              >
                <span className="text-sm text-gray-900">
                  {index + 1}. {EMAIL_MODAL_LABELS[key]}
                </span>
                <ChevronRightIcon className="w-5 h-5 text-shade-blue flex-shrink-0" aria-hidden />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <>
          <button
            type="button"
            className="text-sm text-shade-blue hover:underline mb-3"
            onClick={() => setSelectedModal('')}
          >
            ← Back to email modals
          </button>
          <h2 className="text-base font-semibold text-gray-900 mb-3">{EMAIL_MODAL_LABELS[selectedModal]}</h2>

          <div className="overflow-x-auto border rounded-lg">
            <table className="text-left text-sm w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="py-2 px-3">Party type</th>
                  <th className="py-2 px-3 w-24">To</th>
                  <th className="py-2 px-3 w-24">Cc</th>
                  <th className="py-2 px-3 w-24">Bcc</th>
                  <th className="py-2 px-3 w-24">Hide</th>
                </tr>
              </thead>
              <tbody>
                {partyLabels.map((party: string, index: number) => {
                  const { dir, hide } = normalizePartyDefault(modalPartyMap[party])
                  const rowBg = index % 2 ? 'bg-gray-50/80' : ''
                  return (
                    <tr key={party} className={`border-b ${rowBg}`}>
                      <td className="py-2 px-3">{party}</td>
                      {(['to', 'cc', 'bcc'] as const).map((key) => (
                        <td key={key} className="py-2 px-3">
                          <Checkbox
                            title=""
                            id={`emd-${selectedModal}-${party}-${key}`}
                            checked={dir === key}
                            onChange={(checked) => setPartyDir(party, key, checked)}
                          />
                        </td>
                      ))}
                      <td className="py-2 px-3">
                        <Checkbox
                          title=""
                          id={`emd-${selectedModal}-${party}-hide`}
                          checked={hide}
                          onChange={(checked) => setPartyHide(party, checked)}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
